import React, { useState, useEffect } from "react";
import "./addRoute.css";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import PageHeader from "../../../components/common/PageHeader/PageHeader";
import CloseButton2 from "../../../components/common/CloseButton/CloseButton2";
import MultiSelectSearchDropdownGlobal from "../../../components/common/CombinationBox/MultiSelectSearchDropdownGlobal";

import { GetMap, routeTypes, RouteDetail } from "../../../services/routeApi";

import InputBoxLarge from "../../../components/common/InputBox/InputBoxLarge";
import RouteMap from "./RouteMap";
import { showAlert } from "../../../components/common/ErrorAlert/ErrorAlert";
import EditButton from "../../../components/common/EditButton/EditButton";
import DocumentActions from "../../../components/common/Action-icons/DocumentActions";
import ActivityLogIcon from "../../../components/common/Action-icons/ActivityLogIcon";

const ViewRoutePage = () => {

    const { t } = useTranslation();

    const [routeType, setRouteType] = useState([]);
    const [routeName, setRouteName] = useState("");

    const [routeData, setRouteData] = useState(null);
    const [loadingMap, setLoadingMap] = useState(false);

    const [routeInterval, setRouteInterval] = useState([]);
    const [selectedDay, setSelectedDay] = useState("monday");

    const [routeTypeChoices, setrouteTypeChoices] = useState([]);

    const [dailyMultiSelectOpen, setDailyMultiSelectOpen] = useState(false);
    const [dailyStops, setDailyStops] = useState([]);

    const [selectedMapIndex, setSelectedMapIndex] = useState();
    const [animateEdit, setAnimateEdit] = useState(false);
    const [dailyRouteData, setDailyRouteData] = useState({});
    const [isDailyPreviewSelected, setIsDailyPreviewSelected] = useState(false);

    const { id } = useParams();
    const navigate = useNavigate();

    const fetchRouteDetail = async (id) => {
        try {
            const response = await RouteDetail(id);

            if (response.data) {
                const data = response.data.data;

                setRouteName(data.name);
                setRouteType(data.route_type);

                if (data.route_type === "weekly") {

                    const fetchedIntervals = data.route_intervals.map((rint) => ({
                        day: rint.interval,
                        stops: rint.stop_data.map((s) => ({
                            id: s.id,
                            label: s.label
                        })),
                        polyline: rint.polyline,
                        total_distance: rint.total_distance,
                        total_duration: rint.total_duration,
                        origin: rint.origin,
                        destination: rint.destination,
                        waypoints: rint.waypoints
                    }));

                    setRouteInterval(fetchedIntervals);

                } else {

                    const fetchedStops = data.route_intervals.map((rint) =>
                        rint.stop_data.map((s) => ({
                            id: s.id,
                            label: s.label
                        }))
                    )[0];

                    setDailyStops(fetchedStops);

                    const routeMapData = data.route_intervals.map((rint) => ({
                        polyline: rint.polyline,
                        total_distance: rint.total_distance,
                        total_duration: rint.total_duration,
                        origin: rint.origin,
                        destination: rint.destination,
                        waypoints: rint.waypoints
                    }))[0];

                    setDailyRouteData(routeMapData);
                }
            }
        }catch(error){
            if (error?.response?.data?.error_code === 1001) navigate("/error");
        }
    };

    useEffect(() => {
        if (id) {
            fetchRouteDetail(id);
        }
    }, [id]);


    const fetchRouteTypes = async () => {
        const response = await routeTypes();

        if (response.data) {

            const options = response.data.data.map((item) => ({
                label: item.label,
                value: item.value
            }));

            setrouteTypeChoices(options);
        }
    };

    useEffect(() => {
        fetchRouteTypes();
    }, []);

    const handleClose = () => {
        navigate("/routes");
    };

    const handleMapchange = async (payload) => {
    if (payload.stops.length < 2) return;

    setLoadingMap(true);

    try {
        let md = await GetMap(payload);

        // ✅ SAFE CHECK
        if (!md || !md.data || !md.data.data) {
        showAlert("Selected customers do not have valid latitude/longitude");
        setRouteData(null);
        return;
        }

        setRouteData(md.data.data);

    } catch (error) {
        console.error("Map error:", error);

        showAlert("Unable to load map. Please check customer locations.");
        setRouteData(null);
    } finally {
        setLoadingMap(false);
    }
    };

    const handleMapSelection = (idx) => {

        setSelectedMapIndex(idx);

        const selectedInterval = routeInterval[idx];

        if (selectedInterval && selectedInterval.polyline) {

            const rData = {
                polyline: selectedInterval.polyline,
                total_distance: selectedInterval.total_distance,
                total_duration: selectedInterval.total_duration,
                origin: selectedInterval.origin,
                destination: selectedInterval.destination,
                waypoints: selectedInterval.waypoints
            };

            setLoadingMap(true);
            setRouteData(rData);
            setLoadingMap(false);

            return;
        }

        if (selectedInterval && selectedInterval.stops) {

            const validStops = selectedInterval.stops
                .map((s) => s.id)
                .filter(Boolean);

            if (validStops.length < 2) {
                setRouteData(null);
                return;
            }

            const payload = { stops: validStops };

            handleMapchange(payload);
        }
    };

    const switchDailyMap = () => {

        if (dailyRouteData) {

            setLoadingMap(true);

            setRouteData(dailyRouteData);

            setLoadingMap(false);

            return;
        }

        if (dailyStops) {

            const validStops = dailyStops
                .map((s) => s.id)
                .filter(Boolean);

            if (validStops.length < 2) {

                setRouteData(null);

                return;
            }

            const payload = { stops: validStops };

            handleMapchange(payload);
        }
    };

    const handleEditButtonClick = (id) => {
        navigate("/routes/edit", { state: { id } });
    };

    // animation
    const triggerEditAnimation = () => {
        setAnimateEdit(true);
        setTimeout(() => setAnimateEdit(false), 800);
    };

    const capitalize = (text) => {
        if (!text || typeof text !== "string") return "";
        return text.charAt(0).toUpperCase() + text.slice(1);
    };

    return (
        <div className="whole-container"
            onClickCapture={(e) => {
                if (
                e.target.tagName === "INPUT" ||
                e.target.tagName === "TEXTAREA" ||
                e.target.classList.contains("input-box") ||
                e.target.classList.contains("text-box") ||
                e.target.classList.contains("multi-select-box-container") ||
                e.target.closest(".input-box") ||
                e.target.closest(".text-box") ||
                e.target.closest(".multi-select-box-container")
                ) {
                triggerEditAnimation();
                }
            }}
        >

            <div className="path-header">

                <PageHeader
                    breadcrumbs={[
                        { label: "Route Management" },
                        { label: "Route", path: "/routes" },
                        { label: "Details" }
                    ]}
                />

            </div>

            <div className="page-actions-bar">
                <div className="name-utility">
                    <div className="page-name-add-uom common-text ftw-5">
                        {t("Route Details")}
                    </div>
                    <DocumentActions />
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="2"
                        height="17"
                        viewBox="0 0 2 17"
                        fill="none"
                    >
                        <path d="M1 0V17" stroke="#CDCDCD" />
                    </svg>
                    <ActivityLogIcon/>
                
                </div>
                <div className="page-name-rightside-icons">

                    <div className="page-name-rightside-icons">
                        <EditButton 
                        animate={animateEdit}
                        onClick={() => handleEditButtonClick(id)} />
                        <CloseButton2 onClick={handleClose} />
                    </div>

                </div>

                

            </div>

            <div style={{ marginTop: "1rem" }}>

                <div className="route-content-section">

                    <div className="route-fields-card">

                        <div className="route-detail-box">

                            <span>{t("Route Details")}</span>

                            <div className="route-d-box-container">

                                <InputBoxLarge
                                    label="Name"
                                    defaultValue={routeName}
                                    readOnly
                                />

                                <InputBoxLarge
                                    label="Route Type"
                                    // options={routeTypeChoices}
                                    defaultValue={capitalize(routeType)}
                                    readOnly
                                />

                            </div>

                        </div>

                        {routeType === "weekly" && (
                            <div className="schedule-section">

                                <div className="schedule-header">
                                    {t("Weekly Schedule")}
                                </div>

                                <div className="schedule-body">

                                    <table>

                                        <thead>
                                            <th>{t("Day")}</th>
                                            <th>{t("Customers")}</th>
                                            <th></th>
                                        </thead>

                                        <tbody>

                                            {routeInterval.map((interval, index) => (

                                                <tr key={index}>

                                                    <td>

                                                        <InputBoxLarge
                                                            defaultValue={capitalize(interval.day)}
                                                            readOnly
                                                        />

                                                    </td>

                                                    <td>

                                                        <MultiSelectSearchDropdownGlobal
                                                            key={`${index}-${(interval.stops || []).map(s => s.id).join(',')}`}
                                                            defaultValue={(interval.stops || []).map(s => ({ value: s.id, label: s.name || s.label }))}
                                                            isOpen={false}
                                                            readOnly={true}
                                                        />

                                                    </td>

                                                    <td>
                                                        <div className="action-cols-details">
                                                            <button
                                                                className={`preview-map-button ${selectedMapIndex === index
                                                                    ? "preview-map-button-selected"
                                                                    : ""
                                                                    }`}
                                                                onClick={() => handleMapSelection(index)}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                    <path d="M18 8C18 11.613 14.131 15.429 12.607 16.795C12.4327 16.9282 12.2194 17.0003 12 17.0003C11.7806 17.0003 11.5673 16.9282 11.393 16.795C9.87 15.429 6 11.613 6 8C6 6.4087 6.63214 4.88258 7.75736 3.75736C8.88258 2.63214 10.4087 2 12 2C13.5913 2 15.1174 2.63214 16.2426 3.75736C17.3679 4.88258 18 6.4087 18 8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                                                    <path d="M12 10C13.1046 10 14 9.10457 14 8C14 6.89543 13.1046 6 12 6C10.8954 6 10 6.89543 10 8C10 9.10457 10.8954 10 12 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                                    <path d="M8.71406 14H5.00406C4.79439 14.0001 4.59005 14.0661 4.41994 14.1886C4.24982 14.3112 4.12253 14.4841 4.05606 14.683L2.05206 20.683C2.00182 20.8333 1.98803 20.9934 2.01182 21.1501C2.03561 21.3068 2.0963 21.4556 2.18888 21.5842C2.28147 21.7128 2.40331 21.8176 2.54434 21.8899C2.68538 21.9622 2.84158 21.9999 3.00006 22H21.0001C21.1584 21.9999 21.3145 21.9621 21.4554 21.8899C21.5964 21.8177 21.7181 21.713 21.8107 21.5845C21.9033 21.456 21.964 21.3074 21.9879 21.1508C22.0118 20.9942 21.9981 20.8343 21.9481 20.684L19.9481 14.684C19.8817 14.4848 19.7543 14.3115 19.584 14.1888C19.4137 14.066 19.209 13.9999 18.9991 14H15.2871" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                                                </svg>
                                                                <span>{t("Preview")}</span>
                                                            </button>
                                                        </div>
                                                        

                                                    </td>

                                                </tr>

                                            ))}

                                        </tbody>

                                    </table>

                                </div>

                            </div>
                        )}

                        {routeType === "daily" && (

                            <div className="schedule-section">

                                <div className="schedule-header">
                                    {t("Daily Schedule")}
                                </div>

                                <div className="schedule-body">

                                    <table>
                                        <thead>
                                            <th>{t("Customers")}</th>
                                            <th></th>
                                        </thead>
                                        <tbody>

                                            <tr>

                                                <td>

                                                    <MultiSelectSearchDropdownGlobal
                                                        defaultValue={(dailyStops || []).map(s => ({ value: s.id, label: s.name || s.label }))}
                                                        isOpen={dailyMultiSelectOpen}
                                                        readOnly={true}
                                                    />

                                                </td>

                                                <td>
                                                    <div className="action-cols">
                                                        <button
                                                            className={`preview-map-button ${isDailyPreviewSelected ? "preview-map-button-selected" : ""}`}
                                                            onClick={() => {
                                                                setIsDailyPreviewSelected(true);
                                                                switchDailyMap();
                                                            }}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                <path d="M18 8C18 11.613 14.131 15.429 12.607 16.795C12.4327 16.9282 12.2194 17.0003 12 17.0003C11.7806 17.0003 11.5673 16.9282 11.393 16.795C9.87 15.429 6 11.613 6 8C6 6.4087 6.63214 4.88258 7.75736 3.75736C8.88258 2.63214 10.4087 2 12 2C13.5913 2 15.1174 2.63214 16.2426 3.75736C17.3679 4.88258 18 6.4087 18 8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                                                <path d="M12 10C13.1046 10 14 9.10457 14 8C14 6.89543 13.1046 6 12 6C10.8954 6 10 6.89543 10 8C10 9.10457 10.8954 10 12 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                                <path d="M8.71406 14H5.00406C4.79439 14.0001 4.59005 14.0661 4.41994 14.1886C4.24982 14.3112 4.12253 14.4841 4.05606 14.683L2.05206 20.683C2.00182 20.8333 1.98803 20.9934 2.01182 21.1501C2.03561 21.3068 2.0963 21.4556 2.18888 21.5842C2.28147 21.7128 2.40331 21.8176 2.54434 21.8899C2.68538 21.9622 2.84158 21.9999 3.00006 22H21.0001C21.1584 21.9999 21.3145 21.9621 21.4554 21.8899C21.5964 21.8177 21.7181 21.713 21.8107 21.5845C21.9033 21.456 21.964 21.3074 21.9879 21.1508C22.0118 20.9942 21.9981 20.8343 21.9481 20.684L19.9481 14.684C19.8817 14.4848 19.7543 14.3115 19.584 14.1888C19.4137 14.066 19.209 13.9999 18.9991 14H15.2871" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                                            </svg>
                                                            {t("Preview")}
                                                        </button>
                                                    </div>


                                                </td>

                                            </tr>
                                        </tbody>

                                    </table>

                                </div>

                            </div>

                        )}

                    </div>

                    <div className="route-map-section">

                        {!loadingMap && routeData && (
                            <RouteMap routeData={routeData} />
                        )}

                        {!routeData && (
                            <div className="route-container">
                                <svg xmlns="http://www.w3.org/2000/svg" width="237" height="237" viewBox="0 0 237 237" fill="none">
                                    <circle cx="116.551" cy="117.361" r="87.4711" fill="#EDF5FF" />
                                    <circle cx="46.5362" cy="44.3421" r="5.48252" fill="#B8DDFF" />
                                    <circle cx="70.2238" cy="20.2174" r="10.2174" fill="#DBF0FF" />
                                    <circle cx="36.3069" cy="173.68" r="9.71902" fill="#D3EDFF" />
                                    <circle cx="205.778" cy="143.325" r="2.74126" fill="#F1F3FF" />
                                    <path d="M131.15 43.6136C130.861 43.5522 130.861 43.1398 131.15 43.0783L135.519 42.1493C135.624 42.1269 135.706 42.0448 135.729 41.9396L136.67 37.6015C136.732 37.3139 137.142 37.3139 137.205 37.6015L138.145 41.9396C138.168 42.0448 138.251 42.1269 138.356 42.1493L142.725 43.0783C143.014 43.1398 143.014 43.5522 142.725 43.6136L138.356 44.5427C138.251 44.5651 138.168 44.6471 138.145 44.7524L137.205 49.0905C137.142 49.378 136.732 49.378 136.67 49.0905L135.729 44.7524C135.706 44.6471 135.624 44.5651 135.519 44.5427L131.15 43.6136Z" fill="#C4DDF5" />
                                    <path d="M196.454 169.072C196.554 168.598 197.231 168.598 197.331 169.072L198.83 176.153C198.866 176.325 199.001 176.46 199.173 176.498L206.181 178.025C206.652 178.127 206.652 178.798 206.181 178.901L199.173 180.427C199.001 180.465 198.866 180.6 198.83 180.773L197.331 187.854C197.231 188.328 196.554 188.328 196.454 187.854L194.955 180.773C194.918 180.6 194.784 180.465 194.612 180.427L187.604 178.901C187.133 178.798 187.133 178.127 187.604 178.025L194.612 176.498C194.784 176.46 194.918 176.325 194.955 176.153L196.454 169.072Z" fill="#C4DDF5" />
                                    <path d="M98.0333 40.503C98.1106 40.138 98.6316 40.138 98.7088 40.503L99.3821 43.6839C99.4103 43.8168 99.5137 43.9208 99.6464 43.9497L102.803 44.6375C103.166 44.7164 103.166 45.2332 102.803 45.3122L99.6464 45.9999C99.5137 46.0289 99.4103 46.1329 99.3821 46.2658L98.7088 49.4467C98.6316 49.8117 98.1106 49.8117 98.0333 49.4467L97.36 46.2658C97.3319 46.1329 97.2284 46.0289 97.0957 45.9999L93.9387 45.3122C93.5763 45.2332 93.5763 44.7164 93.9387 44.6375L97.0957 43.9497C97.2284 43.9208 97.3319 43.8168 97.36 43.6839L98.0333 40.503Z" fill="#C4DDF5" />
                                    <path d="M65.4176 144.684C76.6393 141.468 70.174 143.758 76.7063 141.299C81.6391 138.398 86.7312 135.726 92.7511 134.371C97.8652 133.219 97.7402 133.364 103.34 133.639C106.014 134.462 108.227 136.257 108.642 138.364C108.984 140.105 105.477 142.68 103.34 143.629C95.3553 147.177 99.2482 145.314 93.4125 146.936C82.5747 149.949 70.9112 155.103 66.358 163.119C63.887 167.47 63.2881 172.526 65.4492 176.527C68.4751 182.13 77.7562 186.789 86.9734 185.985C90.6685 185.663 94.9734 184.961 98.5199 183.863C102.269 182.702 104.725 181.805 107.962 180.003C115.47 175.826 124.418 170.397 131.735 166.048" stroke="#C4DDF5" stroke-width="2.17374" stroke-linecap="round" stroke-dasharray="4.35 4.35" />
                                    <path d="M159.92 30.6747C188.954 33.334 210.335 59.0268 207.676 88.0608C207.352 91.5911 206.687 95.009 205.717 98.2851C202.522 112.32 192.365 126.409 181.561 138.124C170.727 149.872 159.128 159.352 152.904 164.155C149.893 166.478 145.675 166.114 143.129 163.276C138.385 157.987 130.005 148.126 122.139 136.28C114.288 124.458 106.895 110.579 104.191 97.2469C102.552 91.2899 101.937 84.9432 102.534 78.4305C105.193 49.3965 130.886 28.0154 159.92 30.6747Z" fill="url(#paint0_linear_20809_1361)" stroke="#C4DDF5" stroke-width="1.99364" />
                                    <path d="M156.997 62.6042C168.397 63.6485 176.792 73.737 175.747 85.1369C174.703 96.5366 164.615 104.931 153.216 103.887C141.816 102.843 133.42 92.7554 134.464 81.3556C135.508 69.9556 145.597 61.56 156.997 62.6042Z" fill="white" stroke="#C4DDF5" stroke-width="1.99364" />
                                    <path d="M46.4064 82.5278C57.8071 79.7981 69.2625 86.8273 71.9923 98.228C72.3258 99.6208 72.5131 101.014 72.5653 102.391L72.5668 102.422L72.5696 102.455C73.1657 108.217 71.1044 115.062 68.3483 121.256C65.6063 127.419 62.2442 132.794 60.3978 135.568C59.9032 136.311 58.9598 136.537 58.1904 136.103C55.5689 134.625 50.8706 131.808 46.1177 128.133C41.3499 124.446 36.6032 119.953 33.8083 115.139L33.7911 115.109L33.7712 115.081L33.5134 114.692C32.2451 112.728 31.2822 110.519 30.7062 108.114C27.9764 96.7129 35.0056 85.2576 46.4064 82.5278Z" fill="#C4DDF5" stroke="#C4DDF5" stroke-width="1.99364" />
                                    <path d="M49.145 93.9677C54.2274 92.7507 59.3342 95.8845 60.5513 100.967C61.7682 106.049 58.6346 111.156 53.552 112.373C48.4696 113.59 43.3627 110.456 42.1457 105.374C40.9289 100.292 44.0626 95.1847 49.145 93.9677Z" fill="white" stroke="#C4DDF5" stroke-width="1.99364" />
                                    <defs>
                                        <linearGradient id="paint0_linear_20809_1361" x1="159.829" y1="31.6676" x2="147.456" y2="166.753" gradientUnits="userSpaceOnUse">
                                            <stop stop-color="#C4E1F5" />
                                            <stop offset="1" stop-color="#F1F3FF" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <span>{t("No route preview yet")}</span>
                                <span>{t("Click preview  to view the map.")}</span>
                            </div>
                        )}

                    </div>

                </div>

            </div>

        </div>
    );
};

export default ViewRoutePage;
